import "@tento-ui/init";
import "@tento-ui/brand/brand-app/brand-app";
import "@tento-ui/brand/brand-app/brand-app-content";
import "@tento-ui/brand/header/marketing-header";
import "@tento-ui/brand/landing/landing-footer";
import "../../lib/marketing/lona-marketing-footer";
import "@tento-ui/brand/landing/landing-section";
import "@tento-ui/brand/landing/landing-hero";
import "@tento-ui/brand/landing/landing-card";
import "@tento-ui/brand/landing/landing-grid";
import "@tento-ui/brand/landing/landing-integrations";

import SITE_NAV from "../nav.json" with { type: "json" };
import {
  ComparisonSection,
  CtaSection,
  FaqSection,
  FeatureSection,
  HeroSection,
  IntegrationsSection,
  PricingSection,
  SiteNav,
} from "@tento-ui/brand/@generated/models";
import { BrandApp } from "@tento-ui/brand/brand-app/brand-app";
import { $ } from "@tento-ui/dom-selectors";
import { DomUtils } from "@tento-ui/dom";
import { DarkMode } from "@tento-ui/dark-mode";
import { publicLandingConfig } from "../../lib/brand-app/landing-public";
import { navConfigFromSiteNav } from "../../lib/brand-app/landing-nav";
import { LandingSection } from "@tento-ui/brand/landing/landing-section";
import { LandingSectionIntro } from "@tento-ui/brand/landing/landing-section-intro";
import { LonaLandingDesignSystem } from "../../lib/brand-app/landing-design-system";
import { StepList } from "../../lib/marketing/lona-steps";

// Section content. Shapes mirror routes/brand.pages/features/calendar.json. The
// pricing section renders the real PlanSelector from LonaPlans.PLANS, so it only
// needs heading/subtext (tiers are ignored by renderPricingContent).
const HERO = {
  headline: "Simple pricing for a calendar you can read",
  subheadline:
    "Connect the week you already use and plan on the full surface — tasks, stickers, rows, formulas, and availability — before you pay. When Lona earns a place in your week, pick the plan that fits.",
  primaryCta: { label: "Start with your calendar", href: "/signin" },
  secondaryCta: { label: "Try the calendar", href: "/features/calendar" },
  media: {
    type: "screenshot",
    src: "/@/static/img/landing/cal-all-rows.jpg",
    alt: "A Lona week with events, tasks, stickers, and context rows on one surface",
  },
};

const PRICING = {
  heading: "Pick the plan that fits your week",
  subtext:
    "The trial starts with the full Professional surface — no feature gates while you try it. Annual billing saves 20% or more.",
  billingNote: "Annual billing saves 20% or more",
  tiers: [],
};

// Split into two bento sections of three so they read as paired rows.
const INCLUDED_SURFACE = {
  heading: "What every plan includes",
  subtext:
    "The basics that make the week readable should not sit behind a paywall.",
  features: [
    {
      name: "Your real calendar",
      description:
        "Connect Google Calendar and plan on the week you already use, not a copy of it.",
    },
    {
      name: "The full surface, free first",
      description:
        "Try tasks, stickers, rows, formulas, and live availability before billing ever starts.",
    },
    {
      name: "One surface, not five tabs",
      description:
        "Calendar, tasks, notes, and scheduling stay together in the same week.",
    },
  ],
};

const INCLUDED_TRUST = {
  heading: "No lock-in, ever",
  subtext: "Stay because Lona helps, not because leaving is hard.",
  features: [
    {
      name: "No credit card to start",
      description:
        "Begin planning today and add billing only when you decide on a plan.",
    },
    {
      name: "Cancel anytime",
      description:
        "Change or cancel from your settings in a couple of clicks — no email required.",
    },
    {
      name: "Your data stays yours",
      description:
        "Your calendar context is yours to export and yours to delete, whenever you want.",
    },
  ],
};

const INTEGRATIONS = {
  heading: "Works with what you already use",
  integrations: [
    {
      name: "Google Calendar",
      logoUrl: "/@/static/img/integrations/google-calendar-64-icon.png",
    },
    { name: "Garmin", logoUrl: "/@/static/img/integrations/garmin-64-icon.svg" },
    { name: "Whoop", logoUrl: "/@/static/img/integrations/whoop.svg" },
    { name: "Claude", logoUrl: "/@/static/img/integrations/claude-64-icon.svg" },
    { name: "OpenAI", logoUrl: "/@/static/img/integrations/openai-64-icon.svg" },
    { name: "Gemini", logoUrl: "/@/static/img/integrations/gemini-64-icon.svg" },
  ],
};

const COMPARISON = {
  heading: "Worth more than the tools it replaces",
  subtext:
    "One subscription instead of a calendar, a task app, a scheduling tool, and a notes doc that never quite line up.",
  comparisons: [
    {
      feature:
        "One place to plan::Your week should be readable before you make the next decision.",
      ours: "[yes] Calendar, tasks, context, and availability stay on one live surface",
      theirs: "[partial] A separate app for each, kept in sync by hand",
      competitor: "A stack of separate tools",
    },
    {
      feature:
        "Pay once for the week::Stop paying several subscriptions to plan one week.",
      ours: "[yes] One plan covers calendar, tasks, and notes — Professional folds in your scheduling tool too",
      theirs:
        "[partial] Calendar add-ons, a scheduler, and notes each bill on their own",
      competitor: "Per-tool subscriptions",
    },
    {
      feature:
        "See value before you pay::Billing should start after the product proves itself.",
      ours: "[yes] The full planning surface is free first, with no card to begin",
      theirs: "[no] Trials often gate the very features that show the value",
      competitor: "Feature-gated trials",
    },
  ],
};

const FAQ = {
  heading: "Pricing questions",
  items: [
    {
      question: "Is there a free trial?",
      answer:
        "Yes. The trial is the full Professional surface — tasks, stickers, rows, formulas, and availability — before you pay. There are no feature gates while you try it; pick the plan that fits when Lona has earned a place in your week.",
    },
    {
      question: "Do I need a credit card to start?",
      answer:
        "No. You can start planning today and only add billing when you decide on a plan.",
    },
    {
      question: "Can I cancel anytime?",
      answer:
        "Yes. You can change or cancel your plan in a couple of clicks from your subscription settings, and your access continues until the end of the period you have already paid for.",
    },
    {
      question: "What happens to my data if I cancel?",
      answer:
        "Your calendar stays connected to its original source, and your Lona context remains yours to export or delete. Canceling stops billing — it does not hold your data hostage.",
    },
    {
      question: "What is the difference between Personal and Professional?",
      answer:
        "Personal is for planning your own life — calendar, tasks, stickers, and notes in one weekly view. Professional is for people who share their time for work: it adds availability links that stay true to your real week, plus energy and recovery context for planning around how you actually work.",
    },
    {
      question: "What is the difference between Professional and Teams?",
      answer:
        "Professional is one person sharing availability and planning their own week. Teams adds shared planning surfaces and team availability for coordinating time across people, billed per seat, while personal rows stay private.",
    },
    {
      question: "How much does annual billing save?",
      answer:
        "Annual billing saves 20% or more compared with paying monthly — at least two months free — and you can switch between monthly and annual at any time.",
    },
    {
      question: "Do you support custom or larger rollouts?",
      answer:
        "Yes. Choose Contact Us and we will help with calendar-led workflow design, workspace rollout, and implementation guidance.",
    },
  ],
};

const STEPS = {
  heading: "Start in three steps",
  subtext: "From your first login to a week you can read.",
  features: [
    {
      name: "Connect your calendar",
      description:
        "Sign in with Google Calendar and your real week shows up — nothing to import.",
    },
    {
      name: "Mark what matters",
      description:
        "Add tasks, stickers, and rows so the important days stand out at a glance.",
    },
    {
      name: "Share and plan in flow",
      description:
        "Send availability from the week you trust and plan ahead without switching tabs.",
    },
  ],
};

const CTA = {
  heading: "Start with your calendar",
  subtext:
    "Connect the week you already use, plan on the full surface free, and upgrade only when Lona earns it.",
  primaryCta: { label: "Start with your calendar", href: "/signin" },
  secondaryCta: { label: "See what's inside", href: "/features/calendar" },
};

DarkMode.toggle(false, false);

const config = publicLandingConfig;
const navConfig = navConfigFromSiteNav(SiteNav.parse(SITE_NAV), "current-host");
const sections = config.landing.sections;

// "Start in three steps" — a numbered instruction list. No section factory
// exists for this, so build it inline reusing the standard section chrome
// (LandingSection + LandingSectionIntro) plus the StepList component.
function $stepsSection(): HTMLElement {
  const t = LonaLandingDesignSystem.sections.features;
  const $section = LandingSection.makeWith({
    paddingY: t.paddingY,
    background: t.background,
    gap: t.gap,
    border: t.border,
    first: t.first,
    maxWidth: t.maxWidth,
  });
  $section.appendChild(
    LandingSectionIntro.makeWith({
      heading: STEPS.heading,
      subtext: STEPS.subtext,
    }),
  );
  $section.appendChild(
    StepList.makeWith({
      steps: STEPS.features.map((f) => ({
        title: f.name,
        description: f.description,
      })),
    }),
  );
  return $section;
}

const resolve: BrandApp.Config["resolve"] = () => {
  const $page = document.createElement("div");
  $page.append(
    sections.hero.make(HeroSection.parse(HERO)),
    sections.pricing.make(PricingSection.parse(PRICING)),
    sections.features.make(FeatureSection.parse(INCLUDED_SURFACE)),
    sections.features.make(FeatureSection.parse(INCLUDED_TRUST)),
    sections.integrations.make(IntegrationsSection.parse(INTEGRATIONS)),
    sections.comparison.make(ComparisonSection.parse(COMPARISON)),
    sections.faq.make(FaqSection.parse(FAQ)),
    $stepsSection(),
    sections.cta.make(CtaSection.parse(CTA)),
  );
  return $page;
};

const $app = $<BrandApp>("app");
await $app.render({
  ds: config.brandDS,
  pageStyle: config.landing.style,
  navConfig,
  resolve,
});

await document.fonts.ready;
await DomUtils.hydrateAndReveal();
